import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as networkSelectors from 'data/network/network.selectors';
import * as mediaGatewaySelectors from 'data/mediaGateway/mediaGateway.selectors';
import { RootState } from 'data/root.reducers';
import * as mediaGatewayActions from 'data/mediaGateway/mediaGateway.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { AdaptedMediaGateway } from 'data/mediaGateway/mediaGateway.types';
import MediaGatewayForm from 'routes/MediaGateway/components/MediaGatewayForm';
import * as types from 'data/mediaGateway/mediaGateway.types';
import { FormTemplate } from 'data/template/template.types';
import { mediaGatewayTemplate } from '../../templates';
import {
 adaptTemplateVisibilityMode, adaptTemplateOptions, adaptTemplateMediagatewaynumlist, adaptMediaGatewayData
} from '../../utils/mediaGatewayTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AdaptedMediaGateway
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedMediaGateway: mediaGatewaySelectors
    .getSelectedMediaGateway(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedMediaGateway: mediaGatewaySelectors
    .getSelectedMediaGateway(state)[0],
  networks: networkSelectors.getNetworks(state),
  mediaGateway: mediaGatewaySelectors.getMediaGateway(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToMediaGatewayList: mediaGatewayActions.goToMediaGatewayList,
  goToMediaGatewayEdit: mediaGatewayActions.goToMediaGatewayEdit,
  getMediaGateway: mediaGatewayActions.getMediaGateway,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const MediaGatewayView = ({
  history,
  selectedMediaGateway,
  deliveryFunctionId,
  networks,
  mediaGateway,
  goToMediaGatewayList,
  goToMediaGatewayEdit,
  getMediaGateway,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AdaptedMediaGateway>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(mediaGatewayTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedMediaGateway?.id;
  useEffect(() => {
    if (id) {
      getMediaGateway(String(id));
    }
  }, [id, getMediaGateway]);

  useEffect(() => {
    if (mediaGateway != null && template != null) {
      const clonedMediaGateway = cloneDeep(mediaGateway);
      let clonedTemplate: FormTemplate | undefined = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate = adaptTemplateOptions(clonedTemplate, networks);
      clonedTemplate = adaptTemplateMediagatewaynumlist(clonedTemplate, mediaGateway);

      const adaptedMediaGateway = adaptMediaGatewayData(clonedMediaGateway);

      setTemplate(clonedTemplate);
      setFormData(adaptedMediaGateway);
    }
  }, [mediaGateway, id, networks]);

  const handleClickEdit = () => goToMediaGatewayEdit(history);

  const handleCancel = () => goToMediaGatewayList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedMediaGateway);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <MediaGatewayForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (mediaGateway == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="MediaGatewayView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('mediaGateway.view.title'))}
          <div>
            {renderForm()}
          </div>
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(MediaGatewayView));
